import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import { AccessParamList } from '../../../typings/Params';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import { useAuth } from '../../../context/Auth';
import ActingBodiesUnitsDropdown from '../../dispatch/Units/ActingBodiesUnitsDropdown';
import GenericUserDropdown from '../../../components/Dropdowns/GenericUserDropdown';
import CameraDropdown from '../../../components/access/CameraDropdown';
import GroupPermissionsDropdown from './GroupPermissionsDropdown';
import RolesDropdown from '../../../components/Dropdowns/RolesDropdown';
import WorkScheduleDropdown from '../../../components/Dropdowns/WorkScheduleDropdown';
import TagDropdown from './TagDropdown';

interface Props {
    filters: AccessParamList['Groups'];
    setFilters: React.Dispatch<React.SetStateAction<AccessParamList['Groups']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

function Filter({ filters, setFilters, setTextFilter, textFilter }: Props) {
    const { user: authenticatedUser } = useAuth();

    const [actingBodyId, setActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyUnitIds, setActingBodyUnitIds] = useState<number[]>(filters.actingBodyUnitIds ?? []);
    const [userId, setUserId] = useState<number>(filters.userId ?? 0);
    const [selectedCameraId, setSelectedCameraId] = useState<string>(filters.cameraId ?? '');
    const [groupPermissions, setGroupPermissions] = useState<(keyof GroupPermissions)[]>(filters.groupPermissions ?? []);
    const [roleId, setRoleId] = useState<number>(filters.roleId ?? 0);
    const [userTagId, setUserTagId] = useState<number>(filters.userTagId ?? 0);
    const [agentScheduleId, setAgentScheduleId] = useState<number>(filters.agentScheduleId ?? 0);
    const [patent, setPatent] = useState<string>(filters.patent ?? '');

    const navigation = useNavigation<NavigationProp<AccessParamList, 'Groups'>>();

    const handleCleanFilter = () => {
        setTextFilter('');
        setActingBodyUnitIds([]);
        setActingBodyId(0);
        setUserId(0);
        setSelectedCameraId('');
        setGroupPermissions([]);
        setRoleId(0);
        setUserTagId(0);
        setAgentScheduleId(0);
        setPatent('');
    };

    const handleFilter = () => {
        const applied = {
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitIds: actingBodyUnitIds.length ? actingBodyUnitIds : undefined,
            userId: userId !== 0 ? userId : undefined,
            cameraId: selectedCameraId !== '' ? selectedCameraId : undefined,
            groupPermissions: groupPermissions.length ? groupPermissions : undefined,
            roleId: roleId !== 0 ? roleId : undefined,
            tagId: userTagId !== 0 ? userTagId : undefined,
            agentScheduleId: agentScheduleId !== 0 ? agentScheduleId : undefined,
            patent: patent !== '' ? patent : undefined,
        };

        setFilters({
            ...filters,
            ...applied,
        });

        navigation.setParams({
            ...applied,
            page: 1,
        });
    };

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        } else if (authenticatedUser.isAdmin || authenticatedUser.permissions.access_edit_group_settings_all_acting_bodies) {
            return actingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    const customSetActingBodyId: React.Dispatch<React.SetStateAction<number>> = (value) => {
        setActingBodyId(value);
        setActingBodyUnitIds([]);
        setUserId(0);
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.access_edit_group_settings_all_acting_bodies) &&
                <ActingBodiesDropdown
                    value={actingBodyId}
                    setValue={customSetActingBodyId}
                    zIndex={9} />
            }
            {(authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.access_edit_group_settings_all_acting_bodies
                || authenticatedUser?.permissions.access_edit_group_settings_acting_body_unit) &&
                <ActingBodiesUnitsDropdown
                    actingBodyId={getActingBodyId()}
                    value={actingBodyUnitIds}
                    setValue={setActingBodyUnitIds}
                    zIndex={8} />
            }
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />
            <GenericUserDropdown
                value={userId}
                setValue={setUserId}
                actingBodyId={getActingBodyId()}
                zIndex={7}
                label='user' />
            <CameraDropdown setValue={setSelectedCameraId} value={selectedCameraId} zIndex={6} />
            <RolesDropdown
                actingBodyId={getActingBodyId()}
                value={roleId}
                setValue={setRoleId}
                zIndex={5} />
            <TagDropdown value={userTagId} setValue={setUserTagId} zIndex={4} />
            <WorkScheduleDropdown
                label={translate('workSchedule')}
                actingBodyId={getActingBodyId()}
                value={agentScheduleId}
                setValue={setAgentScheduleId}
                zIndex={3} />
            <FilterInput
                label={translate('patent')}
                value={patent}
                placeholder={translate('patent')}
                onChange={setPatent}
            />
            <GroupPermissionsDropdown
                value={groupPermissions}
                setValue={setGroupPermissions}
                zIndex={1}
            />
        </Filters>
    );
}

export default Filter;
